import { useQuery } from "@apollo/client";
import {
    TOP_PLAYERS_LIFETIME,
    TOP_PLAYERS_CURRENT_SEASON,
    TOP_PLAYERS_PREVIOUS_SEASON,
    TOP_PLAYERS_SHIELD_VALUE,
    ALL_SEASONS,
} from "@/graphql/queries/leaderboardQueries";

import { LeaderboardPlayer, Season } from "@/types/leaderboards";

export interface UseLeaderboardsOptions {
    selectedSeasonId?: string;
    limit?: number;
    lifetimeOffset?: number;
    shieldOffset?: number;
    currentSeasonOffset?: number;
    previousSeasonOffset?: number;
}

export function useLeaderboards({
    selectedSeasonId,
    limit = 50,
    lifetimeOffset = 0,
    shieldOffset = 0,
    currentSeasonOffset = 0,
    previousSeasonOffset = 0,
}: UseLeaderboardsOptions) {
    const lifetime = useQuery<{ topPlayersLifetime: LeaderboardPlayer[] }>(
        TOP_PLAYERS_LIFETIME,
        { variables: { limit, offset: lifetimeOffset } },
    );

    const shield = useQuery<{ topPlayersShieldValue: LeaderboardPlayer[] }>(
        TOP_PLAYERS_SHIELD_VALUE,
        { variables: { limit, offset: shieldOffset } },
    );

    const current = useQuery<{ topPlayersCurrentSeason: LeaderboardPlayer[] }>(
        TOP_PLAYERS_CURRENT_SEASON,
        { variables: { limit, offset: currentSeasonOffset } },
    );

    const previous = useQuery<{
        topPlayersPreviousSeason: LeaderboardPlayer[];
    }>(TOP_PLAYERS_PREVIOUS_SEASON, {
        variables: {
            seasonId: selectedSeasonId,
            limit,
            offset: previousSeasonOffset,
        },
        skip: !selectedSeasonId,
    });

    const seasons = useQuery<{ allSeasons: Season[] }>(ALL_SEASONS);

    return {
        topPlayersLifetime: lifetime.data?.topPlayersLifetime ?? [],
        topPlayersShieldValue: shield.data?.topPlayersShieldValue ?? [],
        topPlayersCurrentSeason: current.data?.topPlayersCurrentSeason ?? [],
        topPlayersPreviousSeason: previous.data?.topPlayersPreviousSeason ?? [],
        allSeasons: seasons.data?.allSeasons ?? [],
        loading:
            lifetime.loading ||
            shield.loading ||
            current.loading ||
            previous.loading ||
            seasons.loading,
        error:
            lifetime.error ||
            shield.error ||
            current.error ||
            previous.error ||
            seasons.error,

        // Expose raw query results for pagination with fetchMore
        lifetimeQuery: lifetime,
        shieldQuery: shield,
        currentSeasonQuery: current,
        previousSeasonQuery: previous,
    };
}
